/**
 * WorklistRail — BLOCK level (Claude Design round 7). The left pane of the new-design Today screen, in
 * the same source order as TodaysAppointmentsView: search · "TODAY, {date}" · the day's cards. The title,
 * the PracticePicker and the bucket tabs sit in the page header above the panes, as on the default Today
 * (Antonin, 15 Oct: "everything sticks to all designs"); the search is the page's (server search).
 */
import { TextField, cn } from '@scripsteam/scrips-signal-ds';
import { V2_BODY } from '@/shared/ui/design-v2';
import type { BucketKey } from '../todays-appointments-view';
import type { TodayItem } from './today-types';
import { WorklistCardItem } from './WorklistCardItem';
import { Search } from 'lucide-react';

/** The current screen's own empty copy (todays-appointments-view.tsx), unchanged. */
const EMPTY_BY_BUCKET: Record<BucketKey, string> = {
  upcoming: 'No appointments booked',
  'in-progress': 'No appointments in session',
  completed: 'No completed appointments',
};

export interface WorklistRailProps {
  search: string;
  onSearch: (v: string) => void;
  /** The active bucket (names the empty message). */
  activeBucket: BucketKey;
  /** The active bucket's appointments, in the page's order. */
  items: TodayItem[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  dateLabel: string;
}

export function WorklistRail(p: WorklistRailProps) {
  return (
    <aside
      aria-label="Today's appointments"
      data-slot="today-worklist"
      className="flex h-full w-85 flex-none flex-col border-0 border-r border-solid border-border-default"
    >
      <div className="flex flex-col gap-3 px-4 pt-4 pb-3">
        <TextField
          size="compact"
          placeholder="Search appointments"
          aria-label="Search appointments"
          value={p.search}
          onChange={(e) => p.onSearch(e.target.value)}
          leadingIcon={<Search className="size-4" />}
        />
      </div>
      <div className="mx-4 h-px bg-border-default" />
      <div className="flex flex-col gap-3 px-4 py-3">
        <span className="text-xs font-normal text-text-secondary">Today, {p.dateLabel}</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden px-4 pt-1 pb-4">
        {p.items.map((it) => (
          <WorklistCardItem key={it.id} item={it} selected={it.id === p.selectedId} onSelect={p.onSelect} />
        ))}
        {p.items.length === 0 && (
          <p className={cn('m-0 px-1 py-2 text-text-secondary', V2_BODY)}>
            {EMPTY_BY_BUCKET[p.activeBucket]}
          </p>
        )}
      </div>
    </aside>
  );
}
